/**
 * web/hud.ts
 * Pure-DOM HUD overlay for the platformer (World 1 / 3+).
 *
 * Reads `engine/hudSnapshot` on a 100 ms throttle (mirrors HUD.tsx /
 * ADR-008) and updates strip text only when values change. The DOM nodes
 * are mounted once; subsequent ticks just mutate `.textContent`.
 *
 * @module web/hud
 */

/// <reference lib="dom" />

import { getHudSnapshot } from '@src/game/engine/hudSnapshot';
import type { ProjectileKind } from '@src/game/entities/Projectile';

const HUD_UPDATE_INTERVAL_MS = 100;
const MAX_HEARTS_DISPLAY = 6;

const WEAPON_GLYPH: Record<Exclude<ProjectileKind, 'star_shard'>, string> = {
  gem_shard: '◆',
  boomerang: '↺',
  fire_orb: '☀',
  ice_crystal: '❄',
};

/** Format a weapon slot string; empty when the default star_shard is active. */
export function formatWeaponSlot(kind: ProjectileKind, ammo: number): string {
  if (kind === 'star_shard') return '';
  return `${WEAPON_GLYPH[kind]} ${ammo}`;
}

/** Format `totalSeconds` as `MM:SS`. */
export function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Build hearts string capped at {@link MAX_HEARTS_DISPLAY} glyphs. */
export function heartsString(health: number, maxHp: number): string {
  const displayMax = Math.min(maxHp, MAX_HEARTS_DISPLAY);
  const filled = Math.max(0, Math.min(health, displayMax));
  const empty = displayMax - filled;
  return '♥'.repeat(filled) + '♡'.repeat(empty);
}

interface HudFields {
  hearts: HTMLSpanElement;
  seeds: HTMLSpanElement;
  shards: HTMLSpanElement;
  score: HTMLSpanElement;
  lives: HTMLSpanElement;
  timer: HTMLSpanElement;
  weapon: HTMLSpanElement;
}

/** Handle for a mounted HUD. */
export interface HudHandle {
  /** Stop polling and remove the strip from the DOM. */
  destroy: () => void;
  /** Pause / resume polling without unmounting (used during overlays). */
  setActive: (active: boolean) => void;
}

const makeSpan = (cls: string): HTMLSpanElement => {
  const el = document.createElement('span');
  el.className = cls;
  return el;
};

/** Mount the HUD strip inside `host` and start a RAF poll. */
export function mountHud(host: HTMLElement): HudHandle {
  const strip = document.createElement('div');
  strip.className = 'sf-hud-strip';
  strip.setAttribute('aria-hidden', 'true');

  const fields: HudFields = {
    hearts: makeSpan('sf-hud-hearts'),
    seeds: makeSpan('sf-hud-text'),
    shards: makeSpan('sf-hud-text sf-hud-shards'),
    score: makeSpan('sf-hud-text sf-hud-score'),
    lives: makeSpan('sf-hud-text sf-hud-lives'),
    timer: makeSpan('sf-hud-text sf-hud-timer'),
    weapon: makeSpan('sf-hud-text sf-hud-weapon'),
  };
  strip.append(
    fields.hearts, fields.seeds, fields.shards,
    fields.weapon, fields.score, fields.lives, fields.timer,
  );
  host.append(strip);

  let last = { health: -1, maxHealth: -1, seeds: -1, shardsC: -1, shardsT: -1,
    score: -1, lives: -1, elapsed: -1, kind: '' as ProjectileKind | '', ammo: -1 };
  let lastUpdate = 0;
  let active = true;
  let rafId = 0;

  const draw = (): void => {
    const s = getHudSnapshot();
    const elapsedSec = Math.floor(s.elapsedSeconds);
    if (s.health === last.health && s.maxHealth === last.maxHealth &&
        s.seeds === last.seeds && s.shardsCollected === last.shardsC &&
        s.shardsTotal === last.shardsT && s.score === last.score &&
        s.lives === last.lives && elapsedSec === last.elapsed &&
        s.projectileKind === last.kind && s.ammo === last.ammo) {
      return;
    }
    fields.hearts.textContent = heartsString(s.health, s.maxHealth);
    fields.seeds.textContent = `★ ${s.seeds}`;
    fields.shards.textContent = `◆ ${s.shardsCollected}/${s.shardsTotal}`;
    fields.score.textContent = s.score.toString();
    fields.lives.textContent = `✕${s.lives}`;
    fields.timer.textContent = formatTime(s.elapsedSeconds);
    fields.weapon.textContent = formatWeaponSlot(s.projectileKind, s.ammo);
    last = {
      health: s.health, maxHealth: s.maxHealth, seeds: s.seeds,
      shardsC: s.shardsCollected, shardsT: s.shardsTotal,
      score: s.score, lives: s.lives, elapsed: elapsedSec,
      kind: s.projectileKind, ammo: s.ammo,
    };
  };

  const tick = (ts: number): void => {
    if (!active) { rafId = 0; return; }
    if (ts - lastUpdate >= HUD_UPDATE_INTERVAL_MS) {
      lastUpdate = ts;
      draw();
    }
    rafId = requestAnimationFrame(tick);
  };

  draw();
  rafId = requestAnimationFrame(tick);

  return {
    destroy: () => {
      active = false;
      if (rafId) cancelAnimationFrame(rafId);
      strip.remove();
    },
    setActive: (next: boolean) => {
      if (next && !active) {
        active = true;
        lastUpdate = 0;
        rafId = requestAnimationFrame(tick);
      } else {
        active = next;
      }
    },
  };
}
